import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { ownerApi, downloadCustomersExport } from "@/lib/api";
import { money } from "@/lib/format";
import { Spinner } from "@/components/common/States";

const SORTS = [
  { key: "recent", label: "Recent" },
  { key: "name", label: "Name A–Z" },
  { key: "orders", label: "Most orders" },
  { key: "points", label: "Most points" },
];

export default function OwnerCustomers() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerCustomers"], queryFn: ownerApi.getCustomers });
  const [reward, setReward] = useState(null);
  const [search, setSearch] = useState("");
  const [dateMode, setDateMode] = useState("any");
  const [dateVal, setDateVal] = useState("");
  const [sort, setSort] = useState("recent");
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ownerCustomers"] });

  const rows = useMemo(() => {
    let list = (query.data ?? []).slice();
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => (c.customer_name || "").toLowerCase().includes(q) || (c.phone || "").includes(q) || (c.email || "").toLowerCase().includes(q));
    }
    if (dateMode !== "any" && dateVal) {
      list = list.filter((c) => {
        const d = (c.updated_at || c.last_activity || "").slice(0, dateMode === "day" ? 10 : 7);
        return d === dateVal;
      });
    }
    const cmp = {
      recent: () => 0,
      name: (a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""),
      orders: (a, b) => (b.completed_orders || 0) - (a.completed_orders || 0),
      points: (a, b) => (b.points || 0) - (a.points || 0),
    }[sort];
    return list.sort(cmp);
  }, [query.data, search, dateMode, dateVal, sort]);

  if (query.isPending) return <Spinner />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile or email…" data-testid="owner-customer-search" className="w-full rounded-full border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary" />
        </div>
        <select value={dateMode} onChange={(e) => { setDateMode(e.target.value); setDateVal(""); }} data-testid="owner-customer-datemode" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          <option value="any">Any date</option>
          <option value="day">A day</option>
          <option value="month">A month</option>
        </select>
        {dateMode === "day" ? <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="owner-customer-date" className="rounded-full border border-border bg-background px-3 py-2 text-xs" /> : null}
        {dateMode === "month" ? <input type="month" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="owner-customer-month" className="rounded-full border border-border bg-background px-3 py-2 text-xs" /> : null}
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="owner-customer-sort" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <button onClick={() => downloadCustomersExport("excel", search.trim())} className="rounded-full border border-border px-4 py-2 text-xs font-semibold">Export Excel</button>
        <button onClick={() => downloadCustomersExport("pdf", search.trim())} className="rounded-full border border-border px-4 py-2 text-xs font-semibold">Export PDF</button>
        <span className="self-center text-xs text-muted-foreground">{rows.length} customer{rows.length === 1 ? "" : "s"}</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-xs text-muted-foreground">
            <tr><th className="p-3">Customer</th><th className="p-3">Points</th><th className="p-3">Orders</th><th className="p-3">Tier</th><th className="p-3"></th></tr>
          </thead>
          <tbody data-testid="owner-customers-table">
            {rows.map((c) => (
              <tr key={c.phone} className="border-t border-border" data-testid={`owner-customer-${c.phone}`}>
                <td className="p-3"><p className="font-medium">{c.customer_name || "—"}</p><p className="text-xs text-muted-foreground">{c.phone}{c.email ? ` · ${c.email}` : ""}</p></td>
                <td className="p-3">{c.points}</td>
                <td className="p-3">{c.completed_orders}</td>
                <td className="p-3">
                  <select value={c.tier} onChange={(e) => ownerApi.setTier(c.phone, e.target.value, "").then(invalidate).then(() => toast.success("Tier updated"))} className="rounded-lg border border-border bg-background p-1.5 text-xs">
                    <option value="none">None</option><option value="basic">Basic</option><option value="premium">Premium</option>
                  </select>
                </td>
                <td className="p-3"><button onClick={() => setReward({ phone: c.phone, name: c.customer_name, amount: 50, note: "" })} className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Reward</button></td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">No customers match your filters.</td></tr> : null}
          </tbody>
        </table>
      </div>

      {reward ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setReward(null)}>
          <div className="w-full max-w-sm space-y-3 rounded-2xl bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold">Send bonus points to {reward.name || reward.phone}</h3>
            <input type="number" value={reward.amount} onChange={(e) => setReward({ ...reward, amount: Number(e.target.value) })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <input placeholder="Note (optional)" value={reward.note} onChange={(e) => setReward({ ...reward, note: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <button onClick={() => ownerApi.sendReward(reward.phone, reward.amount, reward.note).then(() => { setReward(null); invalidate(); toast.success("Reward sent"); }).catch((e) => toast.error(e.message))} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Send {money(reward.amount)} in points</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
